import { ExpandMore } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Link,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import type React from "react";
import { Suspense, useState } from "react";
import iframeCopy from "../../../utills/iframeCopy.ts";
import { isEvyveska } from "../../../utills/iframeVariants.ts";

const formatPhoneNumber = (phone: string | undefined) => {
	if (!phone) {
		return "nevyplněno";
	}
	const cleaned = phone.replace(/\D/g, "");
	const match = cleaned.match(/^(\d{1,3})(\d{3})(\d{3})(\d{3})$/);
	if (match) {
		return `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
	}
	return phone;
};

const ContactsFallBack: React.FC = () => {
	return (
		<>
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
		</>
	);
};

const Contacts: React.FC<{ id: number }> = ({ id }) => {
	const contactEmail = "SZFprodej@spravazeleznic.cz";

	return (
		<Table
			size="small"
      sx={{
        margin: "auto",
        // make rows stack on small containers
        "& .MuiTableRow-root": {
          "@container ad-detail (max-width: 560px)": {
            display: "block",
            paddingY: 1,
            // borderBottom: theme => `1px solid ${theme.palette.divider}`,
          },
        },
        "& .MuiTableCell-root": {
          "@container ad-detail (max-width: 560px)": {
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            paddingX: .75,
          },
        },
        // first cell (label) styling on small
        "& .MuiTableRow-root .MuiTableCell-root:first-of-type": {
          color: "secondary.main",
          fontWeight: 600,
          "@container ad-detail (max-width: 560px)": {
            paddingTop: .5,
            paddingBottom: .5,
            border: 'none',

          },
        },
        // second cell (value) styling on small
        "& .MuiTableRow-root .MuiTableCell-root:last-of-type": {
          "@container ad-detail (max-width: 560px)": {
            paddingTop: .5,
            paddingBottom: .5,
            border: 'none',
          },
        },
        '& .MuiTableRow-root .MuiTableCell-root': {
          paddingTop: 1.5,
          paddingBottom: 1.5,
        },
        '& .MuiTableRow-root:last-child .MuiTableCell-root': {
          borderBottom: 'none',
        }
      }}
		>
			<TableBody>
				<TableRow
					sx={{
            "&:last-child td, &:last-child th": {border: 0},
						"&:hover": (
                isEvyveska()?
                  'none'
                :
                {
                  backgroundColor: "var(--mui-palette-action-hover)",
                }
              ),
					}}
				>
					<TableCell sx={{ width: "50%" }}>
						E-mail
					</TableCell>
					<TableCell>
            {isEvyveska()?
              contactEmail
              : (
                <Link
                  color="secondary"
                  onClick={() => {
                    iframeCopy(contactEmail);
                  }}
                  href={`mailto:${contactEmail}`}
                >
                  {contactEmail}
                </Link>
              )}
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>
	);
};

const ContactsSection: React.FC<{ id: number }> = ({ id }) => {
	const [expandedContact, setExpandedContact] = useState<boolean>(true);
	return (
		<Accordion expanded={expandedContact}>
			<Tooltip
				followCursor={true}
				placement="right"
				title={expandedContact ? "Skrýt kontaktní informace" : "Zobrazit kontaktní informace"}
			>
				<AccordionSummary
					onClick={() => setExpandedContact(!expandedContact)}
					sx={{ backgroundColor: "grey.200" }}
					expandIcon={<ExpandMore fontSize="medium" color="secondary" />}
				>
					<Typography fontWeight="bolder" sx={{ color: "info.main" }} variant="subtitle1" component="div">
						Kontaktní informace
					</Typography>
				</AccordionSummary>
			</Tooltip>
			<AccordionDetails>
				<Suspense fallback={<ContactsFallBack />}>
					<Contacts id={id} />
				</Suspense>
			</AccordionDetails>
		</Accordion>
	);
};

export default ContactsSection;
